// src/pages/billing/StaffPaymentsPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project.service';
import { userPaymentService } from '../../services/userPayment.service';
import * as userService from '../../services/user.service';
import { getLeads } from '../../services/lead.service';
import {
  UserPayment,
  CreateUserPaymentDto,
  PaymentStatus,
  PaymentType
} from '../../types/finance.types';
import { Project } from '../../types/project.types';
import { User } from '../../types/user.types';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  DollarSign,
  Users,
  Check,
  Clock,
  X,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { formatSafeDate } from '../../utils/dateUtils';

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  hourly: 'Hourly',
  per_lead: 'Per Lead',
  per_survey: 'Per Survey',
  per_enrollment: 'Per Enrollment',
  bonus: 'Bonus',
  reimbursement: 'Reimbursement',
  advance: 'Advance (nets at closure)',
  other: 'Other'
};

export default function StaffPaymentsPage() {
  const { userProfile, currentUser } = useAuth();
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<UserPayment | null>(null);
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [paymentToMarkPaid, setPaymentToMarkPaid] = useState<UserPayment | null>(null);
  const [markAsPaidForm, setMarkAsPaidForm] = useState({
    paymentMethod: '',
    externalReference: '',
    paymentNotes: ''
  });

  // Filters
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Field-pay reconciliation rows for the selected project (earned vs advances/paid).
  const [reconRows, setReconRows] = useState<{ userId: string; userName: string; validLeads: number; earned: number; paid: number; owed: number }[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    userId: '',
    userName: '',
    userEmail: '',
    amount: 0,
    paymentType: 'hourly' as PaymentType,
    description: '',
    projectId: '',
    projectName: '',
    periodStart: '',
    periodEnd: '',
    hoursWorked: 0,
    leadsGenerated: 0,
    surveysCompleted: 0,
    enrollmentsCompleted: 0,
    scheduledDate: '',
    paymentMethod: '',
    externalReference: '',
    notes: ''
  });

  const canViewPayments = ['provider_admin', 'provider_user'].includes(userProfile?.role || '');
  const isProviderAdmin = userProfile?.role === 'provider_admin';

  useEffect(() => {
    loadData();
  }, [userProfile]);

  // Field-pay reconciliation for the selected project. Earned = valid leads ×
  // payRate (per lead). "Paid" sums disbursed commission-basis payments —
  // advances + per-lead/enrollment/survey/other (NOT bonus/reimbursement/hourly,
  // which aren't advances against earned). Owed = Earned − Paid = balance at closure.
  useEffect(() => {
    if (projectFilter === 'all') { setReconRows([]); return; }
    const AGAINST_EARNED = new Set(['advance', 'per_lead', 'per_enrollment', 'per_survey', 'other']);
    const project = projects.find(p => p.id === projectFilter);
    const rate = (project as { settings?: { billingInfo?: { payRate?: number } } })?.settings?.billingInfo?.payRate || 0;
    let cancelled = false;
    getLeads({ projectId: projectFilter }).then((leads: any[]) => {
      if (cancelled) return;
      const byUser: Record<string, { validLeads: number; userName: string }> = {};
      leads.forEach(l => {
        if ((l.determination as string || '') !== 'valid') return;
        const uid = (l.capturedBy as string) || (l.createdBy as string);
        if (!uid) return;
        if (!byUser[uid]) byUser[uid] = { validLeads: 0, userName: (l.capturedByName as string) || '' };
        byUser[uid].validLeads++;
      });
      const paidByUser: Record<string, number> = {};
      payments
        .filter(p => p.projectId === projectFilter && p.status === 'paid' && AGAINST_EARNED.has(p.paymentType))
        .forEach(p => { paidByUser[p.userId] = (paidByUser[p.userId] || 0) + p.amount; });
      const ids = new Set<string>([...Object.keys(byUser), ...Object.keys(paidByUser)]);
      const rows = [...ids].map(uid => {
        const validLeads = byUser[uid]?.validLeads || 0;
        const userName = byUser[uid]?.userName || users.find(u => u.uid === uid)?.name || 'Unknown';
        const earned = validLeads * rate;
        const paid = paidByUser[uid] || 0;
        return { userId: uid, userName, validLeads, earned, paid, owed: earned - paid };
      }).filter(r => r.validLeads > 0 || r.paid > 0).sort((a, b) => b.owed - a.owed);
      setReconRows(rows);
    }).catch(() => setReconRows([]));
    return () => { cancelled = true; };
  }, [projectFilter, payments, projects, users]);

  const loadData = async () => {
    if (!userProfile || !canViewPayments) return;

    setLoading(true);
    try {
      const [paymentsData, projectsData, usersData] = await Promise.all([
        userPaymentService.getUserPayments(),
        projectService.getUserProjects(userProfile.uid),
        userService.getUsers()
      ]);

      setPayments(paymentsData);
      setProjects(projectsData);
      // Filter to show only field users for payments
      setUsers(usersData.filter(u => u.role === 'field' || u.role === 'provider_user'));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (userId: string) => {
    const user = users.find(u => u.uid === userId);
    setFormData(prev => ({
      ...prev,
      userId,
      userName: user?.name || '',
      userEmail: user?.email || ''
    }));
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    setFormData(prev => ({
      ...prev,
      projectId: projectId || '',
      projectName: project?.name || ''
    }));
  };

  const handleCreatePayment = async () => {
    if (!currentUser || !formData.userId || !formData.amount || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const paymentData: CreateUserPaymentDto = {
        userId: formData.userId,
        userName: formData.userName,
        userEmail: formData.userEmail,
        amount: formData.amount,
        paymentType: formData.paymentType,
        description: formData.description,
        projectId: formData.projectId || undefined,
        projectName: formData.projectName || undefined,
        periodStart: formData.periodStart ? new Date(formData.periodStart + 'T12:00:00') : undefined,
        periodEnd: formData.periodEnd ? new Date(formData.periodEnd + 'T12:00:00') : undefined,
        metrics: {
          hoursWorked: formData.hoursWorked || undefined,
          leadsGenerated: formData.leadsGenerated || undefined,
          surveysCompleted: formData.surveysCompleted || undefined,
          enrollmentsCompleted: formData.enrollmentsCompleted || undefined
        },
        scheduledDate: formData.scheduledDate ? new Date(formData.scheduledDate + 'T12:00:00') : undefined,
        paymentMethod: formData.paymentMethod || undefined,
        externalReference: formData.externalReference || undefined,
        notes: formData.notes || undefined
      };

      await userPaymentService.createUserPayment(paymentData, currentUser.uid);
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  const handleOpenMarkAsPaidModal = (payment: UserPayment) => {
    setPaymentToMarkPaid(payment);
    setMarkAsPaidForm({
      paymentMethod: '',
      externalReference: '',
      paymentNotes: ''
    });
    setShowMarkAsPaidModal(true);
  };

  const handleConfirmMarkAsPaid = async () => {
    if (!paymentToMarkPaid || !currentUser || !userProfile) return;

    try {
      await userPaymentService.markPaymentAsPaid(
        paymentToMarkPaid.id,
        new Date(),
        markAsPaidForm.externalReference || undefined,
        markAsPaidForm.paymentMethod || undefined,
        currentUser.uid,
        userProfile.name || userProfile.email,
        markAsPaidForm.paymentNotes || undefined
      );
      toast.success('Payment marked as paid');
      setShowMarkAsPaidModal(false);
      setPaymentToMarkPaid(null);
      loadData();
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      toast.error('Failed to mark payment as paid');
    }
  };

  const handleCancelPayment = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to cancel this payment?')) return;

    try {
      await userPaymentService.cancelPayment(paymentId);
      loadData();
    } catch (error) {
      console.error('Error cancelling payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;

    try {
      await userPaymentService.deleteUserPayment(paymentId);
      loadData();
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      userId: '',
      userName: '',
      userEmail: '',
      amount: 0,
      paymentType: 'hourly',
      description: '',
      projectId: '',
      projectName: '',
      periodStart: '',
      periodEnd: '',
      hoursWorked: 0,
      leadsGenerated: 0,
      surveysCompleted: 0,
      enrollmentsCompleted: 0,
      scheduledDate: '',
      paymentMethod: '',
      externalReference: '',
      notes: ''
    });
    setEditingPayment(null);
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    if (projectFilter !== 'all' && payment.projectId !== projectFilter) return false;
    if (statusFilter !== 'all' && payment.status !== statusFilter) return false;
    if (userFilter !== 'all' && payment.userId !== userFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        payment.userName.toLowerCase().includes(query) ||
        payment.description.toLowerCase().includes(query) ||
        payment.projectName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Calculate totals
  const totalPending = filteredPayments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = filteredPayments
    .filter(p => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);

  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-200 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!canViewPayments) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view staff payments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Staff Payments</h1>
            <p className="mt-1 text-sm text-gray-600">
              Track and manage payments to field staff and contractors
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">${totalPending.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold">{filteredPayments.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Field Pay Reconciliation — shown when a specific project is selected */}
        {projectFilter !== 'all' && reconRows.length > 0 && (() => {
          const project = projects.find(p => p.id === projectFilter);
          const rate = (project as { settings?: { billingInfo?: { payRate?: number } } })?.settings?.billingInfo?.payRate || 0;
          const tot = reconRows.reduce((a, r) => ({ earned: a.earned + r.earned, paid: a.paid + r.paid, owed: a.owed + r.owed }), { earned: 0, paid: 0, owed: 0 });
          return (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Field Pay Reconciliation — {project?.name}</h2>
              <p className="text-sm text-gray-500 mb-4">Earned = valid leads × ${rate}/lead. Advances &amp; commission payments subtract → <strong>balance owed at final closure</strong>. (Bonuses &amp; reimbursements excluded.)</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Staff</th>
                      <th className="py-2 pr-4 text-right">Valid Leads</th>
                      <th className="py-2 pr-4 text-right">Earned</th>
                      <th className="py-2 pr-4 text-right">Advances / Paid</th>
                      <th className="py-2 pr-4 text-right">Owed at Closure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconRows.map(r => (
                      <tr key={r.userId} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium text-gray-900">{r.userName}</td>
                        <td className="py-2 pr-4 text-right">{r.validLeads}</td>
                        <td className="py-2 pr-4 text-right">${r.earned.toFixed(2)}</td>
                        <td className="py-2 pr-4 text-right text-gray-600">${r.paid.toFixed(2)}</td>
                        <td className={`py-2 pr-4 text-right font-semibold ${r.owed > 0 ? 'text-green-700' : r.owed < 0 ? 'text-red-600' : 'text-gray-700'}`}>${r.owed.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-semibold text-gray-900">
                      <td className="py-2 pr-4">Total</td>
                      <td></td>
                      <td className="py-2 pr-4 text-right">${tot.earned.toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">${tot.paid.toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right">${tot.owed.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search payments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'all')}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>

            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Staff</option>
              {users.map(user => (
                <option key={user.uid} value={user.uid}>{user.name || user.email}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <LoadingSpinner className="mx-auto" />
                    </td>
                  </tr>
                ) : filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      No payment records found
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map(payment => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium">{payment.userName}</p>
                          <p className="text-xs text-gray-500">{payment.userEmail}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate">{payment.description}</div>
                        {payment.periodStart && payment.periodEnd && (
                          <div className="text-xs text-gray-500">
                            {formatSafeDate(payment.periodStart, 'MMM d')} - {formatSafeDate(payment.periodEnd, 'MMM d, yyyy')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm">{PAYMENT_TYPE_LABELS[payment.paymentType]}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {payment.projectName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                        ${payment.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                        {payment.status === 'paid' && (payment.paymentMethod || payment.externalReference) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {payment.paymentMethod && <span>{payment.paymentMethod}</span>}
                            {payment.paymentMethod && payment.externalReference && <span> - </span>}
                            {payment.externalReference && <span>{payment.externalReference}</span>}
                          </div>
                        )}
                        {payment.status === 'paid' && payment.paymentNotes && (
                          <div className="text-xs text-gray-400 mt-0.5 max-w-[150px] truncate" title={payment.paymentNotes}>
                            {payment.paymentNotes}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {payment.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleOpenMarkAsPaidModal(payment)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Mark as Paid"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleCancelPayment(payment.id)}
                                className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {isProviderAdmin && payment.status !== 'paid' && (
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Payment Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Add Payment Record</h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  <select
                    value={formData.userId}
                    onChange={(e) => handleUserChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select staff member</option>
                    {users.map(user => (
                      <option key={user.uid} value={user.uid}>{user.name || user.email}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.amount || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                        className="w-full pl-7 pr-3 py-2 border rounded-md"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Type *
                    </label>
                    <select
                      value={formData.paymentType}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentType: e.target.value as PaymentType }))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {Object.entries(PAYMENT_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., Weekly payment for June 1-7"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project (optional)
                  </label>
                  <select
                    value={formData.projectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">No project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Period Start
                    </label>
                    <input
                      type="date"
                      value={formData.periodStart}
                      onChange={(e) => setFormData(prev => ({ ...prev, periodStart: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Period End
                    </label>
                    <input
                      type="date"
                      value={formData.periodEnd}
                      onChange={(e) => setFormData(prev => ({ ...prev, periodEnd: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>

                {/* Metrics based on payment type */}
                {formData.paymentType === 'hourly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hours Worked
                    </label>
                    <input
                      type="number"
                      value={formData.hoursWorked || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, hoursWorked: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="0"
                      step="0.5"
                    />
                  </div>
                )}

                {formData.paymentType === 'per_lead' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Leads Generated
                    </label>
                    <input
                      type="number"
                      value={formData.leadsGenerated || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, leadsGenerated: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="0"
                    />
                  </div>
                )}

                {formData.paymentType === 'per_survey' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Surveys Completed
                    </label>
                    <input
                      type="number"
                      value={formData.surveysCompleted || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, surveysCompleted: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="0"
                    />
                  </div>
                )}

                {formData.paymentType === 'per_enrollment' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enrollments Completed
                    </label>
                    <input
                      type="number"
                      value={formData.enrollmentsCompleted || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, enrollmentsCompleted: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="0"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <input
                      type="text"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="e.g., Direct Deposit, Check"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference #
                    </label>
                    <input
                      type="text"
                      value={formData.externalReference}
                      onChange={(e) => setFormData(prev => ({ ...prev, externalReference: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md"
                      placeholder="e.g., Check #1234"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={2}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePayment}
                  disabled={!formData.userId || !formData.amount || !formData.description}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  Add Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark as Paid Modal */}
        {showMarkAsPaidModal && paymentToMarkPaid && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Mark Payment as Paid</h2>
                  <button
                    onClick={() => {
                      setShowMarkAsPaidModal(false);
                      setPaymentToMarkPaid(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Payment Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Payment to</p>
                  <p className="font-medium">{paymentToMarkPaid.userName}</p>
                  <p className="text-2xl font-bold text-green-600 mt-2">
                    ${paymentToMarkPaid.amount.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{paymentToMarkPaid.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={markAsPaidForm.paymentMethod}
                    onChange={(e) => setMarkAsPaidForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select method...</option>
                    <option value="Check">Check</option>
                    <option value="Direct Deposit">Direct Deposit</option>
                    <option value="Venmo">Venmo</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Cash">Cash</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    External Reference
                  </label>
                  <input
                    type="text"
                    value={markAsPaidForm.externalReference}
                    onChange={(e) => setMarkAsPaidForm(prev => ({ ...prev, externalReference: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Check #, Transaction ID, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={markAsPaidForm.paymentNotes}
                    onChange={(e) => setMarkAsPaidForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                    placeholder="Any additional notes about this payment..."
                  />
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowMarkAsPaidModal(false);
                    setPaymentToMarkPaid(null);
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmMarkAsPaid}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Payment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
